import React, { useEffect, useMemo, useState } from 'react';
import { getUsers } from '../../services/api';
import {
  DEFAULT_ACCOUNTABILITY, OWNER_SELF, OWNER_NOMINATE, normalizeAccountability,
} from '../../services/accountability';

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

/**
 * The one question on this surface nobody else can answer.
 *
 * <h3>Why it is asked before anything is built</h3>
 *
 * An ExteLet onboard is a single synchronous call that registers the agent,
 * issues its identity and establishes its ownership record together. There is
 * no pipeline to pause in the middle of, so the question comes first and the
 * trail reports afterwards. Rendering "registered" and "identity issued" above
 * an unanswered question would show work that had not happened yet, which is
 * the one thing a surface claiming to be evidence must never do.
 *
 * The AutoLet path is genuinely different: it pauses mid-pipeline, which is why
 * its decision ({@link GapDecision}) is hosted inside the trail. Same flow,
 * different mechanics underneath.
 *
 * <h3>Two choices here, not three</h3>
 *
 * The v2 {@code AccountabilitySection} also offers inviting somebody who has no
 * account yet — it creates the user, then nominates them. That is a real path
 * and it stays available in the full platform. It is left out here on the same
 * line this surface draws everywhere else: the simple surface takes you through
 * with what you already have, and the full platform is where you go to set up
 * something new mid-flight.
 *
 * <h3>What it does not promise</h3>
 *
 * Both ownership flags are unset and default to shadow, where the promotion
 * gate surfaces its requirement without enforcing it. A nominated agent is not
 * actually held back from anything today. This reports what the record will
 * say and makes no claim about consequences — the gate states its own case at
 * publish, where it is the thing that knows.
 */
/* Declared at module scope, not inside the render body. A component defined
   inside one is a new type on every render, so React unmounts and remounts its
   subtree — which costs a remount per keystroke in the search box below, and
   takes the caret with it. */
function Choice({ value, mode, onPick, title, desc }) {
  const on = mode === value;
  return (
    <button
      type="button"
      className="p7-btn"
      aria-pressed={on}
      onClick={() => onPick(value)}
      style={{
        display: 'block', textAlign: 'left', width: '100%',
        borderColor: on ? 'var(--p7-accent)' : undefined,
        background: on ? 'var(--p7-accent-bg)' : undefined,
      }}
    >
      <span style={{ display: 'block', fontWeight: 600 }}>{title}</span>
      <span style={{ display: 'block', font: 'var(--p7-text-caption)',
                     fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
        {desc}
      </span>
    </button>
  );
}

export default function AccountabilityAsk({ onConfirm, busy = false }) {
  const [mode, setMode] = useState(DEFAULT_ACCOUNTABILITY.ownerMode);
  const [ownerId, setOwnerId] = useState(null);
  const [people, setPeople] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (mode !== OWNER_NOMINATE || loaded) return undefined;
    let alive = true;
    (async () => {
      try {
        const body = unwrap(await getUsers({}));
        const list = Array.isArray(body) ? body : (Array.isArray(body?.content) ? body.content : []);
        if (alive) { setPeople(list); setLoaded(true); }
      } catch {
        // An empty list is honest here: it says we could not offer anyone, and
        // the person can still take it on themselves.
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [mode, loaded]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return people;
    return people.filter((u) => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q));
  }, [people, search]);

  const pick = (value) => { setMode(value); setError(null); };

  const record = () => {
    try {
      const intent = normalizeAccountability({
        ownerMode: mode,
        ownerPrincipalId: mode === OWNER_NOMINATE ? ownerId : null,
        accepted: mode === OWNER_SELF,
      });
      setError(null);
      onConfirm(intent);
    } catch (e) {
      // The same check the provisioning service makes, run early enough that
      // the person can still fix it rather than meeting it as a failed create.
      setError(e.message);
    }
  };

  /* No heading of its own. The trail renders the question as a node and this
     renders the answer inside it — a second heading here would put the question
     on screen twice, once per owner. */
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Choice value={OWNER_SELF} mode={mode} onPick={pick}
                title="I&rsquo;ll answer for it"
                desc="Attested in one step, in your name." />
        <Choice value={OWNER_NOMINATE} mode={mode} onPick={pick}
                title="Name someone else"
                desc="They are asked to accept. The record shows them as nominated until they do." />
      </div>

      {mode === OWNER_NOMINATE ? (
        <div style={{ marginTop: 'var(--p7-space-4)' }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email"
            aria-label="Search people"
            className="p7-input"
            style={{ width: '100%', padding: '9px 11px',
                     border: '1px solid var(--p7-line-strong)',
                     borderRadius: 'var(--p7-radius-control)',
                     font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)' }}
          />
          <div style={{ marginTop: '8px', maxHeight: '220px', overflowY: 'auto' }}>
            {shown.map((u) => (
              <button
                key={u.id}
                type="button"
                className="p7-btn"
                aria-pressed={ownerId === u.id}
                onClick={() => { setOwnerId(u.id); setError(null); }}
                style={{
                  display: 'block', textAlign: 'left', width: '100%', marginBottom: '5px',
                  borderColor: ownerId === u.id ? 'var(--p7-accent)' : undefined,
                  background: ownerId === u.id ? 'var(--p7-accent-bg)' : undefined,
                }}
              >
                <span style={{ fontWeight: 600 }}>{u.name || u.email}</span>
                {u.name && u.email ? (
                  <span style={{ marginLeft: 8, color: 'var(--p7-ink-subtle)' }}>{u.email}</span>
                ) : null}
              </button>
            ))}
            {loaded && shown.length === 0 ? (
              <p style={{ margin: 0, font: 'var(--p7-text-caption)',
                          fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
                Nobody to show. You can take it on yourself, or add people in the full platform.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="p7-note p7-note--deny" style={{ marginTop: 'var(--p7-space-3)' }}>
          {error}
        </div>
      ) : null}

      <div style={{ marginTop: 'var(--p7-space-5)' }}>
        <button type="button" className="p7-btn p7-btn--primary" disabled={busy} onClick={record}>
          {busy ? 'Recording…' : 'Record and continue'}
        </button>
      </div>
    </div>
  );
}
